import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Spinner } from 'react-bootstrap';
import { useAuth } from '../../../context/AuthContext';
import consultationService from '../../../services/consultationService';
import medicalPrescriptionService from '../../../services/medicalPrescriptionService';
import { useDoctorWorkspace } from '../doctorWorkspaceContext';
import { getDoctorRoutePaths, physicianCanPrescribe, isHeadDoctorRole } from '../doctorPaths';
import { DOCTOR_COPY } from '../doctorCopy';
import { PhysicianPageHeader, PhysicianStatCard, PhysicianHubCard } from '../ui';
import ClinicalButton from '../ui/ClinicalButton';
import Icon from '../../common/Icons';
import '../PhysicianDashboard.css';

const P = DOCTOR_COPY.PHYSICIAN_DASHBOARD;
const D = DOCTOR_COPY.DASHBOARD;
const HUB = DOCTOR_COPY.HUB_CARD_DEFS_PHYSICIAN;

export default function DoctorDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    workSiteId,
    statsReloadNonce,
    openNewConsultation,
    openNewPrescription,
    canPrescribe,
    portalPaths,
    clinicalWorkspace
  } = useDoctorWorkspace();
  const routePaths = useMemo(() => getDoctorRoutePaths(portalPaths), [portalPaths]);

  const [loadingStats, setLoadingStats] = useState(true);
  const [stats, setStats] = useState({
    consultations: { total: 0, completed: 0, cancelled: 0, pending: 0 },
    prescriptions: { total: 0, active: 0, fulfilled: 0 }
  });

  const isDentist = clinicalWorkspace === 'dentist';
  const isHeadDoctor = isHeadDoctorRole(user?.role);

  const pageTitle = isDentist ? P.PAGE_TITLE_DENTIST : P.PAGE_TITLE_PHYSICIAN;
  const pageSubtitle = isDentist ? P.PAGE_SUBTITLE_DENTIST : P.PAGE_SUBTITLE_PHYSICIAN;
  const pageBadge = isDentist ? P.BADGE_DENTIST : P.BADGE_PHYSICIAN;

  const hubCards = useMemo(
    () =>
      HUB.filter((c) => {
        if (c.requiresPrescriber && !canPrescribe) return false;
        if (c.requiresHeadDoctor && !isHeadDoctor) return false;
        return true;
      }),
    [canPrescribe, isHeadDoctor]
  );

  const loadStats = useCallback(async () => {
    if (!user) return;
    setLoadingStats(true);
    try {
      const statFilters =
        workSiteId && String(workSiteId) !== '' ? { site_id: parseInt(workSiteId, 10) } : {};
      const consultationStats = await consultationService.getConsultationStats(statFilters);

      if (consultationStats.success) {
        setStats((prev) => ({
          ...prev,
          consultations: {
            total: consultationStats.data?.total_consultations || 0,
            completed: consultationStats.data?.completed_count || 0,
            cancelled: consultationStats.data?.cancelled_count || 0,
            pending: consultationStats.data?.pending_count || 0
          }
        }));
      }

      if (physicianCanPrescribe(user.role) && user.id) {
        const prescriptionStats = await medicalPrescriptionService.getPrescriptionStats({
          doctor_id: user.id
        });
        if (prescriptionStats.success) {
          setStats((prev) => ({
            ...prev,
            prescriptions: {
              total: prescriptionStats.data?.total_prescriptions || 0,
              active: prescriptionStats.data?.active_count || 0,
              fulfilled: prescriptionStats.data?.fulfilled_count || 0
            }
          }));
        }
      } else {
        setStats((prev) => ({
          ...prev,
          prescriptions: { total: 0, active: 0, fulfilled: 0 }
        }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingStats(false);
    }
  }, [user, workSiteId]);

  useEffect(() => {
    loadStats();
  }, [loadStats, statsReloadNonce]);

  const handleHubCardClick = (def) => {
    if (def.kind === 'newConsult') {
      openNewConsultation();
      return;
    }
    const path = routePaths[def.routeKey];
    if (path) navigate(path);
  };

  const badgeLabel = (badge) => (badge === 'ready' ? P.BADGE_READY : P.BADGE_SOON);

  const renderHubSection = (title, desc, cards) => {
    if (!cards.length) return null;
    return (
      <section className="ph-hub-section">
        <div className="ph-hub-section__head">
          <h2 className="ph-hub-section__title">{title}</h2>
          <p className="ph-hub-section__desc">{desc}</p>
        </div>
        <div className="ph-hub-grid">
          {cards.map((def) => (
            <PhysicianHubCard
              key={`${def.kind}-${def.routeKey || def.title}`}
              def={def}
              badgeLabel={badgeLabel(def.badge)}
              onClick={() => handleHubCardClick(def)}
              onPrescriptionLink={canPrescribe ? openNewPrescription : undefined}
            />
          ))}
        </div>
      </section>
    );
  };

  return (
    <div className="ph-dash">
      <PhysicianPageHeader
        badge={pageBadge}
        title={pageTitle}
        description={pageSubtitle}
        actions={
          <ClinicalButton
            variant="secondary"
            icon="refresh"
            onClick={loadStats}
            disabled={!workSiteId || loadingStats}
          >
            {P.REFRESH}
          </ClinicalButton>
        }
      />

      <section className="ph-dash-primary mb-4" aria-label={P.PRIMARY_TITLE}>
        <div className="mb-3">
          <h2 className="ph-dash-primary__title mb-1">{P.PRIMARY_TITLE}</h2>
          <p className="ph-dash-primary__desc mb-0">{P.PRIMARY_DESC}</p>
        </div>
        <Row className="g-3">
          <Col md={4}>
            <button
              type="button"
              className="ph-quick-card ph-quick-card--accent"
              onClick={openNewConsultation}
              disabled={!workSiteId}
            >
              <span className="ph-quick-card__icon">
                <Icon name="plus" size={20} />
              </span>
              <span>
                <div className="ph-quick-card__title">{P.QUICK_NEW_CONSULT}</div>
                <div className="ph-quick-card__hint">{P.QUICK_NEW_CONSULT_HINT}</div>
              </span>
            </button>
          </Col>
          <Col md={4}>
            <button
              type="button"
              className="ph-quick-card"
              onClick={() => navigate(`${portalPaths.CONSULTATION_NEW}?direct=1`)}
              disabled={!workSiteId}
            >
              <span className="ph-quick-card__icon">
                <Icon name="stethoscope" size={20} />
              </span>
              <span>
                <div className="ph-quick-card__title">{P.QUICK_FULL_CARE}</div>
                <div className="ph-quick-card__hint">{P.QUICK_FULL_CARE_HINT}</div>
              </span>
            </button>
          </Col>
          <Col md={4}>
            <button
              type="button"
              className="ph-quick-card"
              onClick={() => navigate(routePaths.DOSSIER)}
            >
              <span className="ph-quick-card__icon">
                <Icon name="history" size={20} />
              </span>
              <span>
                <div className="ph-quick-card__title">{P.QUICK_DOSSIER}</div>
                <div className="ph-quick-card__hint">{P.QUICK_DOSSIER_HINT}</div>
              </span>
            </button>
          </Col>
        </Row>
      </section>

      <section className="ph-panel mb-4" aria-label={P.STATS_TITLE}>
        <div className="ph-panel__head">
          <h2 className="ph-dash-primary__title mb-1">{P.STATS_TITLE}</h2>
          <p className="ph-dash-primary__desc mb-0">{P.STATS_SUBTITLE}</p>
        </div>
        <div className="ph-panel__body">
          {loadingStats ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          ) : (
            <div className="ph-stats-grid">
              <PhysicianStatCard
                title={P.STAT_CONSULT_TOTAL}
                value={stats.consultations.total}
                icon="chartBar"
                tone="teal"
                hint={P.STAT_HINT_CONSULT}
                loading={loadingStats}
                onClick={() => navigate(routePaths.CONSULTATIONS)}
              />
              <PhysicianStatCard
                title={P.STAT_CONSULT_PENDING}
                value={stats.consultations.pending}
                icon="infoCircle"
                tone="amber"
                hint={P.STAT_HINT_PENDING}
                loading={loadingStats}
                onClick={() => navigate(routePaths.CONSULTATIONS)}
              />
              <PhysicianStatCard
                title={P.STAT_CONSULT_DONE}
                value={stats.consultations.completed}
                icon="checkCircle"
                tone="indigo"
                hint={P.STAT_HINT_DONE}
                loading={loadingStats}
                onClick={() => navigate(routePaths.CONSULTATIONS)}
              />
              {canPrescribe ? (
                <PhysicianStatCard
                  title={P.STAT_RX_ACTIVE}
                  value={stats.prescriptions.active}
                  icon="pills"
                  tone="slate"
                  hint={P.STAT_HINT_RX}
                  subtitle={`${stats.prescriptions.total} au total`}
                  loading={loadingStats}
                  onClick={() => navigate(routePaths.PRESCRIPTIONS)}
                />
              ) : (
                <PhysicianStatCard
                  title={D.STAT_CANCELLED}
                  value={stats.consultations.cancelled}
                  icon="exclamationCircle"
                  tone="slate"
                  hint={P.STAT_HINT_CONSULT}
                  loading={loadingStats}
                  onClick={() => navigate(routePaths.CONSULTATIONS)}
                />
              )}
            </div>
          )}
        </div>
      </section>

      <div className="ph-panel">
        <div className="ph-panel__head">
          <h2 className="ph-dash-primary__title mb-0">{D.HUB_SECTION_TITLE}</h2>
          <p className="ph-dash-primary__desc mb-0 mt-1 small">{D.HUB_SECTION_SUBTITLE}</p>
        </div>
        <div className="ph-panel__body pt-2">
          {renderHubSection(P.HUB_CLINICAL_TITLE, P.HUB_CLINICAL_SUBTITLE, hubCards)}
        </div>
      </div>
    </div>
  );
}
